import * as XLSX from 'xlsx';

/**
 * Exports an HTML table with the given ID to an Excel file with the given filename.
 * @param tableId - The ID of the HTML table element to export.
 * @param fileName - The filename of the exported Excel file.
 */
export function exportTableToExcel(tableId: string, fileName: string): void {
  // Get the HTML table element by ID
  let element = document.getElementById(tableId);
  
  // Check if the element exists to avoid errors
  if (!element) {
    console.error(`Table with ID ${tableId} not found.`);
    return;
  }

  // Convert the HTML table to a worksheet
  const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(element);

  // Create a new workbook and append the worksheet
  const wb: XLSX.WorkBook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

  // Write the workbook to a file with the specified filename
  XLSX.writeFile(wb, fileName);
}



/**
 * Returns the month name for a given index (0 for January, 11 for December).
 * @param index - The index of the month (0-11).
 * @returns The name of the month or 'Invalid month' if the index is out of range.
 */
export function getMonthName(index: number): string {
  const allMonths = [
    'January', 
    'February', 
    'March', 
    'April', 
    'May', 
    'June',
    'July', 
    'August', 
    'September', 
    'October', 
    'November', 
    'December'
  ];

  return allMonths[index] ?? 'Invalid month';
}

 
/**
 * Sanitizes the content of the editor by removing empty tags.
 * @param content - The content to sanitize.
 * @returns - Sanitized content or an empty string.
 */
export function sanitizeEditorContent(content: string): string {
  // Remove tags like <p></p> or <br> if they are the only content
  const sanitized = content.replace(/<[^>]*>/g, '').trim();
  return sanitized === '' ? '' : content;
}


/**
 * Checks if the file size is less than or equal to the given max size.
 *
 * @param file The file to check.
 * @param maxSizeMB Maximum allowed file size in megabytes (default is 20 MB).
 * @returns true if the file size is within the limit, false otherwise.
 */
export function isValidFileSize(fileSize: number, maxSizeMB: number): boolean {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return fileSize <= maxSizeBytes;
}



/**
 * Checks if the given file is a valid image or video and if its
 * width and height are within the given limits.
 *
 * @param file The file to check.
 * @param maxWidth The maximum allowed width of the image or video.
 * @param maxHeight The maximum allowed height of the image or video.
 * @returns A promise resolving to true if the file is valid and its
 * dimensions are within the limits, false otherwise.
 */
export async function isValidMediaDimensions(file: File, maxWidth: number, maxHeight: number): Promise<boolean> {
  if (file.type.startsWith('image/')) {
    return await checkImageDimensions(file, maxWidth, maxHeight);
  } else if (file.type.startsWith('video/')) {
    return await checkVideoDimensions(file, maxWidth, maxHeight);
  } else {
    return true;
  }
}

/**
 * Checks if the given image file's dimensions match the specified limits.
 *
 * @param file - The image file to check.
 * @param maxWidth - The maximum allowed width of the image.
 * @param maxHeight - The maximum allowed height of the image.
 * @returns A promise that resolves to true if the image dimensions
 *          are equal to the specified maxWidth and maxHeight, false otherwise.
 *
 * This function creates an image object and sets its source to the file's
 * object URL. Once the image is loaded, it checks the dimensions and resolves
 * the promise. If there is an error loading the image, it logs the error and
 * resolves the promise with false.
 */

async function checkImageDimensions(file: File, maxWidth: number, maxHeight: number): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      // Log the dimensions to the console
      console.log("Uploaded image dimensions:", img.width, "x", img.height);
      // Resolve with true if dimensions are within limits, false otherwise
      resolve(img.width == maxWidth && img.height == maxHeight);
    };
    img.onerror = (error) => {
      console.error("Error loading image", error);
      resolve(false);
    };
    img.src = URL.createObjectURL(file);
  });
}


/**
 * Checks if the given file is a valid video and if its width and height are within the given limits.
 *
 * @param file The file to check.
 * @param maxWidth The maximum allowed width of the video.
 * @param maxHeight The maximum allowed height of the video.
 * @returns A promise resolving to true if the file is valid and its dimensions are within the limits, false otherwise.
 */
async function checkVideoDimensions(file: File, maxWidth: number, maxHeight: number): Promise<boolean> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      console.log("Uploaded video dimensions:", video.videoWidth, "x", video.videoHeight);
      resolve(video.videoWidth == maxWidth && video.videoHeight == maxHeight);
    };
    video.onerror = (error) => {
      console.error("Error loading video", error);
      reject(error);
    };
    video.src = URL.createObjectURL(file);
  });
}

/**
 * Checks if a file has a mime type that is allowed for import.
 * Currently, only .xlsx files are supported.
 * @param selectedFile The file to check.
 * @returns true if the file is allowed, false otherwise.
 */
export function allowedMimeTypes(selectedFile: File) {
  const allowedMimeTypes = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    // 'application/vnd.ms-excel', // .xls  Uncomment this if older Excel file types need to be supported.
  ];
 return allowedMimeTypes.includes(selectedFile.type)
}
